/**
 * heicCache.js
 *
 * A singleton, module-level cache for HEIC → JPEG conversions.
 *
 * Key design decisions:
 *  1. Module-level Map  → survives React re-renders and re-mounts of components,
 *     acting as a fast in-memory cache for the lifetime of the browser session.
 *  2. In-flight deduplication → if 10 thumbnails of the same HEIC URL mount at
 *     the same time, only ONE fetch+conversion runs; the rest await the same Promise.
 *  3. Object URLs are stored in the cache; a separate ref-count map tracks how many
 *     React components are currently using each entry so we can safely revoke the
 *     Blob URL only when the last consumer unmounts (no premature cleanup, no leaks).
 *  4. LRU eviction cap → keeps memory bounded. Oldest entries are evicted and their
 *     Blob URLs revoked when the cache exceeds MAX_ENTRIES.
 */

import heic2any from 'heic2any';

// ─── Configuration ────────────────────────────────────────────────────────────
const MAX_ENTRIES = 100; // max cached object-URLs before LRU eviction

// ─── Internal state ───────────────────────────────────────────────────────────

/**
 * cache: url → { objectUrl: string, refCount: number }
 * Using a Map preserves insertion order, which we exploit for LRU eviction.
 */
const cache = new Map();

/**
 * inFlight: url → Promise<string>
 * While a conversion is running every subscriber for the same URL awaits
 * this same Promise instead of starting a parallel network + CPU operation.
 */
const inFlight = new Map();

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns true if the given MIME type or URL string indicates a HEIC/HEIF file. */
export const isHeicUrl = (url = '', mimeType = '') => {
    const lowerMime = mimeType.toLowerCase();
    const lowerUrl = url.toLowerCase();
    return (
        lowerMime.includes('heic') ||
        lowerMime.includes('heif') ||
        lowerUrl.endsWith('.heic') ||
        lowerUrl.endsWith('.heif')
    );
};

/** Evict the oldest entry when the cache is full. */
const evictOldestIfNeeded = () => {
    if (cache.size >= MAX_ENTRIES) {
        // Map iteration is insertion-order → first key is oldest
        const [oldestUrl, entry] = cache.entries().next().value;
        cache.delete(oldestUrl);
        // Only revoke the Blob URL if nothing is still rendering it
        if (entry.refCount <= 0) {
            try { URL.revokeObjectURL(entry.objectUrl); } catch (_) { /* ignore */ }
        }
    }
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * getConvertedUrl(url, mimeType)
 *
 * Returns a Promise that resolves to a renderable URL:
 * - For non-HEIC URLs → resolves immediately with the original URL.
 * - For HEIC URLs     → fetches, converts, and caches. Subsequent calls with
 *   the same URL resolve from cache without any network/CPU work.
 *
 * Also increments the internal ref-count so cleanup knows when it's safe to
 * revoke the Blob URL.
 */
export const getConvertedUrl = async (url, mimeType = '') => {
    if (!url) return url;

    // Non-HEIC → pass through unchanged
    if (!isHeicUrl(url, mimeType)) return url;

    // Cache hit → bump ref-count (re-use to front for LRU) and return immediately
    if (cache.has(url)) {
        const entry = cache.get(url);
        entry.refCount += 1;
        // Move to end (most-recently-used) by re-inserting
        cache.delete(url);
        cache.set(url, entry);
        return entry.objectUrl;
    }

    // In-flight deduplication → join existing conversion
    if (inFlight.has(url)) {
        const objectUrl = await inFlight.get(url);
        // The conversion completed; bump ref-count from cache
        if (cache.has(url)) cache.get(url).refCount += 1;
        return objectUrl;
    }

    // Start a new conversion
    const conversionPromise = (async () => {
        try {
            const res = await fetch(url, { cache: 'force-cache' });
            const blob = await res.blob();
            const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.85 });
            const outputBlob = Array.isArray(converted) ? converted[0] : converted;
            const objectUrl = URL.createObjectURL(outputBlob);

            evictOldestIfNeeded();
            cache.set(url, { objectUrl, refCount: 1 });
            return objectUrl;
        } catch (err) {
            console.error('[heicCache] Conversion failed for:', url, err);
            // On failure, store the original URL so we don't retry on every render
            evictOldestIfNeeded();
            cache.set(url, { objectUrl: url, refCount: 1 });
            return url;
        } finally {
            inFlight.delete(url);
        }
    })();

    inFlight.set(url, conversionPromise);
    return conversionPromise;
};

/**
 * releaseUrl(url)
 *
 * Call this when a component unmounts or stops displaying the image.
 * Decrements the ref-count; if it reaches 0 AND the entry has been evicted
 * from the LRU cache, the Blob URL is revoked to free memory.
 *
 * If the entry is still in the cache the Blob URL is kept alive (another
 * component might mount and use it soon). Blob URLs for cache entries are
 * only revoked during LRU eviction.
 */
export const releaseUrl = (url) => {
    if (!url || !isHeicUrl(url)) return;
    if (!cache.has(url)) return;

    const entry = cache.get(url);
    entry.refCount = Math.max(0, entry.refCount - 1);
};

/**
 * clearCache()
 *
 * Revokes all Blob URLs and empties the cache. Useful in low-memory
 * situations or during logout. Components already rendering an image
 * may briefly show a broken image — acceptable for a forced flush.
 */
export const clearCache = () => {
    cache.forEach(({ objectUrl }) => {
        try { URL.revokeObjectURL(objectUrl); } catch (_) { /* ignore */ }
    });
    cache.clear();
    inFlight.clear();
};

/**
 * getCacheStats()
 *
 * Returns a plain object with diagnostic information (useful during dev).
 */
export const getCacheStats = () => ({
    cachedEntries: cache.size,
    inFlightEntries: inFlight.size,
});
